/* eslint-disable css-modules/no-unused-class  */
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { translate } from 'translations';
import { selectToken, selectTenantName } from '@redux/slices/userSlice';
import { useGetSkillTrees } from '@queries/core/skillTree';
import { ContentCardProps, Loader } from 'learning-panel-components';
import { useGetLearningPaths } from '@queries/core/learningPath';
import { convertStatus, progressOrder, filterHiddenLearningPaths } from '@components/learningPath/utils';
import convertSkillTreeToContentCard from '@services/convertSkillTreeToContentCard';
import { coreService } from 'network-utils';
import convertLearningPathToContentCard from '@services/convertLearningPathToContentCard';
import convertSkillTreeToBanner from '@services/convertSkillTreeToBanner';
import { LearningPathBannerProps as LearningPathBanner } from '@components/learningPath/learningPathBanner/learningPathBanner';
import { SkillTreeBannerProps as SkillTreeBanner } from '@components/skillTree/skillTreeBanner/skillTreeBanner';
import CardsGalleryContainer from '@components/cardsGallery/cardsGalleryContainer';
import HomePageBanners from '@components/assessments/homePageBanners/homePageBanners';
import styles from './careerJourney.module.scss';
import EmptySkillTree from './emptySkillTree';

const CareerJourneyContainer: FC<CareerJourneyContainerProps> = (props: CareerJourneyContainerProps) => {
    const { fullWidth } = props;
    const token = useSelector(selectToken);
    const tenantName = useSelector(selectTenantName);
    const { data: skillTreesData } = useGetSkillTrees(token);
    const { data: learningPathsData } = useGetLearningPaths(token);
    const [learningPaths, setLearningPaths] = useState<ContentCardProps[]>([]);
    const [learningPathsBanner, setLearningPathsBanner] = useState<LearningPathBanner[]>([]);
    const [skillTree, setSkillTrees] = useState<ContentCardProps[]>([]);
    const [skillTreeBanner, setSkillTreesBanner] = useState<SkillTreeBanner[]>([]);
    const [fetchingData, setFetchingData] = useState<boolean>(true);

    useEffect(() => {
        if (!learningPathsData) return;
        if ((learningPathsData as any).success === false) return;
        const visibleLearningPathsData = filterHiddenLearningPaths(learningPathsData, tenantName);
        const learningPaths = visibleLearningPathsData.map((learningPath: coreService.LearningPath) =>
            convertLearningPathToContentCard(learningPath)
        );
        learningPaths.sort((a, b) => {
            const indexA = progressOrder.indexOf(a.status);
            const indexB = progressOrder.indexOf(b.status);
            return indexA - indexB;
        });

        setLearningPaths(learningPaths);
        const learningPathsBanner: LearningPathBanner[] = visibleLearningPathsData
            .filter(learningPath => learningPath.progress?.status !== 'completed')
            .map(learningPath => {
                return {
                    name: learningPath.name,
                    description: learningPath.description,
                    duration: learningPath.duration,
                    uuid: learningPath.uuid,
                    status: convertStatus(learningPath.progress?.status) || 'notStarted'
                };
            })
            .slice(0, 3);

        setLearningPathsBanner(learningPathsBanner);
    }, [learningPathsData, tenantName]);

    useEffect(() => {
        if (!skillTreesData) return;
        if ((skillTreesData as any).success === false) return;
        setFetchingData(true);
        const skillTrees = skillTreesData.map((skillTree: coreService.SkillTree) =>
            convertSkillTreeToContentCard(skillTree)
        );
        skillTrees.sort((a, b) => {
            const indexA = progressOrder.indexOf(a.status);
            const indexB = progressOrder.indexOf(b.status);
            return indexA - indexB;
        });
        setSkillTrees(skillTrees);

        const skillTreesBanner: SkillTreeBanner[] = skillTreesData
            .filter((skillTree: coreService.SkillTree) => skillTree.progress?.status !== 'completed')
            .map((skillTree: coreService.SkillTree) => {
                return convertSkillTreeToBanner(skillTree);
            })
            .slice(0, 3);

        setSkillTreesBanner(skillTreesBanner);
        setFetchingData(false);
    }, [skillTreesData]);

    return (
        <div id="career-page" className={styles['career-journey-wrapper']}>
            {fetchingData ? (
                <Loader showBackground={false} />
            ) : (
                <>
                    <HomePageBanners
                        assessments={[]}
                        upComingTournaments={[]}
                        learningPaths={learningPathsBanner}
                        skillTreeBanner={skillTreeBanner}
                        videoTutorialsBanner={[]}
                        showDivider={false}
                        courseBanner={[]}
                        lessonsBanner={[]}
                        labBanner={[]}
                    />
                    <div className={fullWidth ? styles.container : styles['no-full-container']}>
                        <CardsGalleryContainer
                            rows={2}
                            columns={2}
                            data={learningPaths}
                            title={translate('LearningPaths')}
                            noFullWidthColumn={3}
                            fullWidth={fullWidth}
                        />
                        <div className={styles.space} />
                        {skillTree.length !== 0 && !fetchingData && (
                            <CardsGalleryContainer
                                rows={2}
                                columns={2}
                                data={skillTree}
                                title={translate('SkillTrees')}
                                noFullWidthColumn={3}
                                fullWidth={fullWidth}
                            />
                        )}
                        {skillTree.length === 0 && <EmptySkillTree />}
                    </div>
                </>
            )}
        </div>
    );
};

interface CareerJourneyContainerProps {
    fullWidth?: boolean;
}

export default CareerJourneyContainer;
